import React from "react";
import { Upload, Pencil, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/hooks/use-toast";

const featuresAI = [
  "Save time with automatic form scanning",
  "Reduces errors in manual data entry",
  "Works with printed forms or handwritten applications",
];

const featuresManual = [
  "Step-by-step guided application process",
  "Save progress and continue later",
  "Help text for each section of the application",
];

const CreateConsumerApplication: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="page-title">New Consumer Application</p>
        <p className="page-subtitle">
          Create a new consumer application by completing all required steps.
        </p>
      </div>
      <div className="mb-6">
        <div className="rounded-lg border bg-background p-4">
          <p className="page-subheading">Choose Your Application Method</p>
          <p className="page-subtitle">
            We offer two convenient ways to complete your application. Use our
            AI-powered form scanner for quick processing, or enter your
            information manually with our step-by-step guided process.
          </p>
        </div>
      </div>
      <div className="flex gap-6">
        {/* AI-Powered Upload */}
        <div className="flex-1 border rounded-xl p-6 bg-background flex flex-col justify-between shadow-sm">
          <div className="flex flex-col items-center mb-4">
            <div className="bg-primary/10 rounded-lg p-6 mb-4">
              <Upload className="w-12 h-12 text-primary" />
            </div>
            <div className="text-lg font-semibold mb-2 text-center">
              AI-Powered Upload
            </div>
            <div className="text-sm text-muted-foreground text-center mb-2">
              Scan your completed paper application form with our AI
              <br />
              Our AI will automatically extract information from your form
            </div>
          </div>
          <ul className="mb-6 space-y-2">
            {featuresAI.map((feature) => (
              <li key={feature} className="flex items-center text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                {feature}
              </li>
            ))}
          </ul>
          <button
            className="w-full py-2 px-4 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition"
            type="button"
            onClick={() => {
              navigate("/cx/consumer-management/create/ai-upload");
            }}
          >
            <span className="inline-flex items-center justify-center">
              <Upload className="w-4 h-4 mr-2" /> Smart Upload
            </span>
          </button>
        </div>
        {/* Enter Manually */}
        <div className="flex-1 border-2 border-primary rounded-xl p-6 bg-background flex flex-col justify-between shadow-md">
          <div className="flex flex-col items-center mb-4">
            <div className="bg-primary/10 rounded-lg p-6 mb-4">
              <Pencil className="w-12 h-12 text-primary" />
            </div>
            <div className="text-lg font-semibold mb-2 text-center text-primary">
              Enter Manually
            </div>
            <div className="text-sm text-muted-foreground text-center mb-2">
              Fill out the application form step by step
              <br />
              Complete the application form online with our guided process
            </div>
          </div>
          <ul className="mb-6 space-y-2">
            {featuresManual.map((feature) => (
              <li key={feature} className="flex items-center text-sm">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                {feature}
              </li>
            ))}
          </ul>
          <button
            className="w-full py-2 px-4 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition"
            type="button"
            onClick={() => {
              // 🗑️ Remove the stored consumer data before navigation
              localStorage.removeItem("consumerFirstStepData");
              navigate("/cx/consumer-management/create/manual");
            }}
          >
            <span className="inline-flex items-center justify-center">
              <Pencil className="w-4 h-4 mr-2" /> Start Manual Entry
            </span>
          </button>
        </div>
      </div>
      <div className="flex justify-center mt-8">
        <button
          className="text-sm text-primary underline hover:text-primary/80"
          type="button"
          onClick={() => {
            toast({
              title: "Coming Soon",
              description:
                "This feature is not available yet. Please contact support for assistance.",
              variant: "destructive",
            });
          }}
        >
          Having trouble deciding?
        </button>
      </div>
    </div>
  );
};

export default CreateConsumerApplication;
